/**
 * Blue PZ（NOTAM）の天井超過の警告の出し方（docs/08 §228）。
 *
 * 2026-11-01 19:24 に、夜（飛行のない時間）なのに他機（クルー）の「天井を528ft超過」の警告が出た。
 * 原因は、受信している端末の**最新の位置が古いまま残り続ける**のに、鮮度を見ずに天井を判定していたこと
 * （端末が送信をやめた時点の位置・高度の判定が、そのまま出続ける）。直し方は2つ:
 * - 最新の位置が古い（既定5分より前）端末は判定しない
 * - 警告を利用者が消せる（今の超過が解消するまで消す／その機体の警告を止める）
 */

/** この時間より古い位置は、警告の判定に使わない（端末が止まったあとの古い高度で警告し続けないため） */
export const NOTAM_FIX_MAX_AGE_MS = 5 * 60 * 1000;

/** 位置の時刻（UTCミリ秒）が、判定に使える新しさか。未来（時計のずれ）は新しいとみなす */
export function isFreshForAlert(fixTimeMs: number, nowMs: number, maxAgeMs = NOTAM_FIX_MAX_AGE_MS): boolean {
  return nowMs - fixTimeMs <= maxAgeMs;
}

export interface NotamAlert {
  /** 警告を見分ける鍵（`<端末ID>:<エリア名>`、自機は `self:<エリア名>`） */
  key: string;
  /** 端末ID。自機は null（機体ごとに止める対象にしない） */
  terminalId: string | null;
}

/**
 * 出す警告だけを選ぶ。
 * - `muted` の端末（利用者が「この機体は止める」にしたもの）は出さない
 * - `dismissed` の鍵（利用者が「消す」にしたもの）は出さない。ただし**超過が解消した鍵は`dismissed`から外す**
 *   （あとでまた超過したら、もう一度出す）。`dismissed`はこの関数が更新する
 */
export function selectAlerts<T extends NotamAlert>(
  all: readonly T[],
  dismissed: Set<string>,
  muted: ReadonlySet<string>,
): T[] {
  const active = new Set(all.map((a) => a.key));
  for (const key of [...dismissed]) if (!active.has(key)) dismissed.delete(key);
  return all.filter((a) => !dismissed.has(a.key) && !(a.terminalId !== null && muted.has(a.terminalId)));
}
